/**
 * Human-agent operational API client.
 *
 * All functions use same-origin relative paths so requests route through the
 * API gateway (Kong / Vite proxy) and pick up the injected auth headers.
 *
 * Auth: every call includes Authorization: Bearer + optional X-Workspace-Id.
 * Errors: 401 → caller should trigger re-auth; 403 → surface to user; others → throw.
 */
import { getBearerAuthHeaders } from "@/lib/authSession";
import { fetchWithAuthRetry } from "@/lib/authedRequest";
import { inboxWorkspaceHeaders } from "@/lib/inboxLiveConfig";

// ---------------------------------------------------------------------------
// Low-level helpers
// ---------------------------------------------------------------------------

/**
 * @param {string} path  e.g. "/api/v1/human-agent/send-message"
 * @param {object} opts
 * @param {string} [opts.method]
 * @param {Record<string, unknown>} [opts.body]
 * @param {string | null} [opts.workspaceId]
 */
async function _opsFetch(path, { method = "POST", body, workspaceId } = {}) {
  const buildRequest = () => {
    const url = new URL(path, (import.meta.env?.VITE_API_BASE_URL || window.location.origin));
    return fetch(url.toString(), {
      method,
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...getBearerAuthHeaders(),
        ...(workspaceId ? inboxWorkspaceHeaders(workspaceId) : {}),
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  };
  const res = await fetchWithAuthRetry(buildRequest);
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
      if (j?.error) detail = j.error;
    } catch {
      /* ignore */
    }
    const err = new Error(detail || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  // 204 No Content — return null
  if (res.status === 204) return null;
  return res.json();
}

// ---------------------------------------------------------------------------
// Send message / internal note
// ---------------------------------------------------------------------------

/**
 * @param {{ sessionId: string, message: string, messageType?: "chat" | "note", workspaceId?: string | null, attachmentIds?: string[] }} params
 */
export async function postSendMessage({ sessionId, message, messageType = "chat", workspaceId, attachmentIds }) {
  const trimmed = (message || "").trim();
  const ids = Array.isArray(attachmentIds) ? attachmentIds.filter(Boolean) : [];
  if (!sessionId) throw new Error("sessionId is required");
  if (!trimmed && !ids.length) throw new Error("message or attachmentIds required");
  const body = {
    session_id: sessionId,
    message: trimmed || "[Attachment]",
    message_type: messageType,
  };
  if (ids.length) body.attachment_ids = ids;
  return _opsFetch("/api/v1/human-agent/send-message", {
    body,
    workspaceId: workspaceId ?? null,
  });
}

/**
 * Soft-delete the caller's latest overall public message in a session.
 * @param {{ sessionId: string, messageId: string | number, workspaceId: string }} params
 */
export async function deleteLastAgentMessage({ sessionId, messageId, workspaceId }) {
  const numericMessageId = String(messageId ?? "").replace(/^hm-/i, "");
  if (!sessionId) throw new Error("sessionId is required");
  if (!/^\d+$/.test(numericMessageId)) throw new Error("messageId is invalid");
  if (!workspaceId) throw new Error("workspaceId is required");
  return _opsFetch(
    `/api/v1/human-agent/sessions/${encodeURIComponent(sessionId)}/messages/${numericMessageId}`,
    {
      method: "DELETE",
      workspaceId,
    },
  );
}

// ---------------------------------------------------------------------------
// Resolve / Reopen
// ---------------------------------------------------------------------------

/**
 * @param {{ sessionId: string, workspaceId?: string | null }} params
 */
export async function postResolveSession({ sessionId, workspaceId }) {
  if (!sessionId) throw new Error("sessionId is required");
  return _opsFetch("/api/v1/human-agent/resolve-session", {
    body: { session_id: sessionId },
    workspaceId: workspaceId ?? null,
  });
}

/**
 * @param {{ sessionId: string, workspaceId?: string | null }} params
 */
export async function postReopenSession({ sessionId, workspaceId }) {
  if (!sessionId) throw new Error("sessionId is required");
  return _opsFetch("/api/v1/human-agent/reopen-session", {
    body: { session_id: sessionId },
    workspaceId: workspaceId ?? null,
  });
}

// ---------------------------------------------------------------------------
// Reassign (transfer)
// ---------------------------------------------------------------------------

/**
 * @param {{ sessionId: string, toAgentId: number, workspaceId?: string | null }} params
 */
export async function postReassignSession({ sessionId, toAgentId, workspaceId }) {
  if (!sessionId) throw new Error("sessionId is required");
  if (!toAgentId) throw new Error("toAgentId is required");
  return _opsFetch(`/api/v1/agent/conversations/${encodeURIComponent(sessionId)}/reassign`, {
    body: { to_agent_id: toAgentId },
    workspaceId: workspaceId ?? null,
  });
}

// ---------------------------------------------------------------------------
// Assignable agents list
// ---------------------------------------------------------------------------

/**
 * @param {{ excludeSessionId?: string, workspaceId?: string | null }} [params]
 * @returns {Promise<Array<{ id: number, name: string, email: string, is_online: boolean }>>}
 */
export async function fetchAssignableAgents({ excludeSessionId, workspaceId } = {}) {
  const buildRequest = () => {
    const url = new URL("/api/v1/agent/assignable-agents", (import.meta.env?.VITE_API_BASE_URL || window.location.origin));
    if (excludeSessionId) url.searchParams.set("exclude_session_id", excludeSessionId);
    return fetch(url.toString(), {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...getBearerAuthHeaders(),
        ...(workspaceId ? inboxWorkspaceHeaders(workspaceId) : {}),
      },
    });
  };
  const res = await fetchWithAuthRetry(buildRequest);
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
    } catch {
      /* ignore */
    }
    const err = new Error(detail || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  const json = await res.json();
  return Array.isArray(json) ? json : [];
}
